import { Alert, Button, Chip, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Body from "../components/layout/Body";
import useApiHook from "../hooks/useApiHook";
import PatientAssistantSidebar from "../components/ai/PatientAssistantSidebar";
import PatientAssistantResults from "../components/ai/PatientAssistantResults";
import PatientAssistantProgressDialog from "../components/ai/PatientAssistantProgressDialog";
import { ANALYSIS_FUNCTIONS, EXTRA_FUNCTIONS } from "../components/ai/catalog";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

const ACTIVE_STATUSES = ['queued', 'preparing_sources', 'chunking', 'processing_chunks', 'aggregating', 'cancelling'];
const TERMINAL_STATUSES = ['completed', 'partial', 'failed', 'cancelled'];
const STATUS_META = {
    queued: {
        label: 'Analisi in coda',
        description: 'La richiesta e stata presa in carico ed e in attesa del turno sul servizio AI.'
    },
    preparing_sources: {
        label: 'Preparazione analisi',
        description: 'Stiamo preparando i contenuti per l\'analisi.'
    },
    chunking: {
        label: 'Preparazione analisi',
        description: 'Stiamo preparando i contenuti per l\'analisi.'
    },
    processing_chunks: {
        label: 'Analisi in corso',
        description: 'L\'analisi AI della seduta e in elaborazione.'
    },
    aggregating: {
        label: 'Finalizzazione risultati',
        description: 'Stiamo completando e organizzando i risultati.'
    },
    cancelling: {
        label: 'Annullamento in corso',
        description: 'Stiamo interrompendo l\'analisi richiesta.'
    },
    cancelled: {
        label: 'Analisi annullata',
        description: 'L\'analisi e stata annullata.'
    },
    completed: {
        label: 'Analisi completata',
        description: 'I risultati AI sono pronti per la consultazione.'
    },
    partial: {
        label: 'Analisi completata parzialmente',
        description: 'I risultati sono disponibili, ma potrebbero essere incompleti.'
    },
    failed: {
        label: 'Analisi non completata',
        description: 'Si e verificato un problema durante l\'analisi.'
    }
};
const ORIENTATION_ANALYSIS_KEY = 'case_formulation';

const toggleItem = (currentItems, item) => {
    if (currentItems.includes(item)) {
        return currentItems.filter((currentItem) => currentItem !== item);
    }

    return [...currentItems, item];
};

const toggleExclusiveItem = (currentItems, item) => (
    currentItems.includes(item) ? [] : [item]
);

const hasJobMissingFiles = (job, files) => {
    const jobFileIds = Array.isArray(job?.selectedFileIds) ? job.selectedFileIds : [];
    const availableFileIds = new Set((Array.isArray(files) ? files : []).map((file) => Number(file.id)));

    if (jobFileIds.length === 0) {
        return false;
    }

    return jobFileIds.some((fileId) => !availableFileIds.has(Number(fileId)));
};

const jobMatchesRequestedFile = (job, requestedFileId) => {
    if (!requestedFileId) {
        return true;
    }

    const jobFileIds = Array.isArray(job?.selectedFileIds) ? job.selectedFileIds : [];
    return jobFileIds.length === 1 && Number(jobFileIds[0]) === Number(requestedFileId);
};

const getJobSupportTitle = (job, orientation = null) => {
    if (!job) {
        return "Analizza con l'AI";
    }

    const analysisFunctions = Array.isArray(job.analysisFunctions) ? job.analysisFunctions : [];
    const extraFunctions = Array.isArray(job.extraFunctions) ? job.extraFunctions : [];

    if (analysisFunctions.length > 0) {
        return orientation
            ? `Analisi seduta - ${orientation}`
            : 'Analisi seduta';
    }

    if (extraFunctions.length > 0) {
        return 'Monitoraggio percorso';
    }

    return 'Risultato Analisi AI';
};

const formatRetryAvailableAt = (completedAt, retryAfterMs) => {
    if (!completedAt || !retryAfterMs) {
        return null;
    }

    const retryAvailableAtMs = new Date(completedAt).getTime() + Number(retryAfterMs);
    if (Number.isNaN(retryAvailableAtMs)) {
        return null;
    }

    return new Intl.DateTimeFormat('it-IT', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    }).format(new Date(retryAvailableAtMs));
};

const PatientAssistantPage = () => {
    const { patientId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { response: patientResponse, makeRequest: loadPatient, error: patientError } = useApiHook();
    const { makeRequest: createJobRequest, loading: creatingJob, error: createJobError } = useApiHook();
    const { makeRequest: cancelJobRequest, loading: cancellingJob, error: cancelJobError } = useApiHook();
    const { makeRequest: loadJobRequest } = useApiHook();
    const { makeRequest: loadJobResultRequest } = useApiHook();
    const { makeRequest: loadPatientJobsRequest } = useApiHook();
    const [selectedFileIds, setSelectedFileIds] = useState([]);
    const [selectedAnalysis, setSelectedAnalysis] = useState([]);
    const [selectedExtra, setSelectedExtra] = useState([]);
    const [jobResult, setJobResult] = useState(null);
    const [activeJobId, setActiveJobId] = useState(null);
    const [pageError, setPageError] = useState(null);
    const [isProgressDialogOpen, setIsProgressDialogOpen] = useState(false);
    const requestedFileId = Number(searchParams.get('fileId')) || null;

    const loadLatestJob = useCallback(async (files = []) => {
        const response = await loadPatientJobsRequest(`/ai/folders/${patientId}/jobs`);
        if (response?.state !== 'success') {
            return;
        }

        const latestJob = Array.isArray(response.data) ? response.data[0] : null;
        if (!latestJob) {
            setActiveJobId(null);
            setJobResult(null);
            return;
        }

        if (hasJobMissingFiles(latestJob, files)) {
            setActiveJobId(null);
            setJobResult(null);
            return;
        }

        if (!jobMatchesRequestedFile(latestJob, requestedFileId)) {
            setActiveJobId(null);
            setJobResult(null);
            return;
        }

        setActiveJobId(latestJob.id);
        const latestResult = await loadJobResultRequest(`/ai/jobs/${latestJob.id}/result`);
        if (latestResult?.state === 'success') {
            setJobResult(latestResult.data);
        }
    }, [patientId, requestedFileId, loadPatientJobsRequest, loadJobResultRequest]);

    useEffect(() => {
        const initializePage = async () => {
            setPageError(null);
            setActiveJobId(null);
            setJobResult(null);
            setSelectedAnalysis([ORIENTATION_ANALYSIS_KEY]);
            setSelectedExtra([]);

            const patientData = await loadPatient(`/folder/${patientId}`);

            if (patientData?.state === 'success') {
                const patientFiles = Array.isArray(patientData.data?.files) ? patientData.data.files : [];
                const requestedFileExists = requestedFileId
                    ? patientFiles.some((file) => Number(file.id) === Number(requestedFileId) && file.status === 'done')
                    : false;

                setSelectedFileIds(requestedFileExists ? [requestedFileId] : []);
                await loadLatestJob(patientData.data?.files || []);
            }
        };

        initializePage();
    }, [patientId, requestedFileId, loadLatestJob, loadPatient]);

    useEffect(() => {
        if (patientError && patientId) {
            navigate('/dashboard');
        }
    }, [patientError, patientId, navigate]);

    useEffect(() => {
        if (!activeJobId) {
            return undefined;
        }

        let intervalId = null;
        let cancelled = false;

        const pollJob = async () => {
            const jobResponse = await loadJobRequest(`/ai/jobs/${activeJobId}`);
            if (cancelled || jobResponse?.state !== 'success') {
                return;
            }

            const job = jobResponse.data;
            setJobResult((currentValue) => ({
                ...(currentValue || { analysis: [], extra: [] }),
                job
            }));

            if (TERMINAL_STATUSES.includes(job.status)) {
                const resultResponse = await loadJobResultRequest(`/ai/jobs/${activeJobId}/result`);
                if (!cancelled && resultResponse?.state === 'success') {
                    setJobResult(resultResponse.data);
                }

                if (intervalId) {
                    clearInterval(intervalId);
                }
            }
        };

        pollJob();
        intervalId = setInterval(pollJob, 3000);

        return () => {
            cancelled = true;
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [activeJobId, loadJobRequest, loadJobResultRequest]);

    const files = useMemo(() => patientResponse?.data?.files || [], [patientResponse]);
    const availableFiles = useMemo(() => files.filter((file) => file.status === 'done'), [files]);
    const isJobActive = ACTIVE_STATUSES.includes(jobResult?.job?.status);
    const pageJob = jobResult?.job || null;
    const pageJobMeta = pageJob ? (STATUS_META[pageJob.status] || {
        label: 'Analisi in corso',
        description: 'Stiamo elaborando la richiesta.'
    }) : null;
    const pageJobSeverity = (pageJob?.status === 'failed' || pageJob?.status === 'cancelled')
        ? 'error'
        : (pageJob?.status === 'completed' ? 'success' : 'info');
    const pageOrientation = pageJob?.psychologistOrientationSnapshot || null;
    const pageTitle = getJobSupportTitle(jobResult?.job, pageOrientation);
    const completedChipSx = pageJob?.status === 'completed'
        ? {
            backgroundColor: '#d8f3dc',
            color: '#2d6a4f',
            border: '1px solid #b7e4c7'
        }
        : undefined;
    const retryAvailableAtLabel = formatRetryAvailableAt(pageJob?.completedAt, pageJob?.retryAfterMs);

    useEffect(() => {
        if (isJobActive) {
            setIsProgressDialogOpen(true);
            return;
        }

        setIsProgressDialogOpen(false);
    }, [isJobActive]);

    useEffect(() => {
        if (TERMINAL_STATUSES.includes(pageJob?.status)) {
            setIsProgressDialogOpen(false);
        }
    }, [pageJob?.status]);

    const toggleFile = useCallback((fileId) => {
        setSelectedFileIds((currentItems) => toggleItem(currentItems, fileId));
    }, []);

    const toggleAnalysis = useCallback((functionKey) => {
        setSelectedAnalysis((currentItems) => toggleExclusiveItem(currentItems, functionKey));
    }, []);

    const toggleExtra = useCallback((functionKey) => {
        setSelectedExtra((currentItems) => toggleExclusiveItem(currentItems, functionKey));
    }, []);

    const startJob = useCallback(async (jobConfig = null) => {
        setPageError(null);

        const nextSelectedFileIds = Array.isArray(jobConfig?.fileIds) ? jobConfig.fileIds : selectedFileIds;
        const nextSelectedAnalysis = [ORIENTATION_ANALYSIS_KEY];
        const nextSelectedExtra = [];

        setSelectedFileIds(nextSelectedFileIds);
        setSelectedAnalysis(nextSelectedAnalysis);
        setSelectedExtra(nextSelectedExtra);

        const response = await createJobRequest('/ai/jobs', 'post', {
            folderId: Number(patientId),
            fileIds: nextSelectedFileIds,
            analysisFunctions: nextSelectedAnalysis,
            extraFunctions: nextSelectedExtra
        });

        if (response?.state !== 'success') {
            setPageError(response?.error?.message || 'Impossibile avviare il job AI.');
            return false;
        }

        setActiveJobId(response.data.job.id);
        setJobResult({
            job: response.data.job,
            analysis: [],
            extra: []
        });
        return true;
    }, [createJobRequest, patientId, selectedAnalysis, selectedExtra, selectedFileIds]);

    const cancelJob = useCallback(async () => {
        if (!activeJobId) {
            return;
        }

        setPageError(null);
        const response = await cancelJobRequest(`/ai/jobs/${activeJobId}/cancel`, 'post');

        if (response?.state !== 'success') {
            setPageError(response?.error?.message || 'Impossibile annullare il job AI.');
            return;
        }

        setJobResult((currentValue) => ({
            ...(currentValue || { analysis: [], extra: [] }),
            job: response.data.job
        }));
    }, [activeJobId, cancelJobRequest]);

    return (
        <Body
            sideBar={(
                <PatientAssistantSidebar
                    patientName={patientResponse?.data?.name || ''}
                    files={availableFiles}
                    selectedFileIds={selectedFileIds}
                    selectedAnalysis={selectedAnalysis}
                    selectedExtra={selectedExtra}
                    analysisCatalog={ANALYSIS_FUNCTIONS}
                    extraCatalog={EXTRA_FUNCTIONS}
                    onToggleFile={toggleFile}
                    onToggleAnalysis={toggleAnalysis}
                    onToggleExtra={toggleExtra}
                    onStart={startJob}
                    onCancel={cancelJob}
                    loading={creatingJob || cancellingJob}
                    isJobActive={isJobActive}
                />
            )}
            mainElement={(
                <>
                    <PatientAssistantProgressDialog
                        open={isProgressDialogOpen && !!pageJob}
                        job={pageJob}
                        statusMeta={pageJobMeta}
                        severity={pageJobSeverity}
                        onClose={() => setIsProgressDialogOpen(false)}
                    />
                    <Stack spacing={3}>
                        <Stack spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'start', sm: 'center' }}>
                            <Stack width="100%" direction="row" justifyContent="space-between" alignItems="center" spacing={0.5}>
                                <Typography variant="h4" fontWeight="bold">{pageTitle}</Typography>
                                <Button startIcon={<ChevronLeftIcon />} onClick={() => navigate(`/patient/${patientId}`)}>
                                    Torna indietro
                                </Button>
                            </Stack>
                            <Stack width="100%" direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                                {pageJob && (
                                    <>
                                        <Chip label={pageJobMeta.label} color={pageJobSeverity} size="small" sx={completedChipSx} />
                                        {pageOrientation && (
                                            <Chip
                                                label={`Orientamento usato: ${pageOrientation}`}
                                                variant="outlined"
                                                size="small"
                                            />
                                        )}
                                    </>
                                )}
                            </Stack>
                        </Stack>

                        {(pageError || createJobError || cancelJobError || pageJob?.errorMessage) && (
                            <Alert severity="error">
                                {pageError || createJobError?.error?.message || cancelJobError?.error?.message || pageJob?.errorMessage || 'Errore durante la richiesta AI.'}
                            </Alert>
                        )}

                        {pageJob?.retryAfterMs && retryAvailableAtLabel && (
                            <Alert severity="warning">
                                Potrai riprovare dalle {retryAvailableAtLabel}.
                            </Alert>
                        )}

                        <PatientAssistantResults result={jobResult} orientation={pageOrientation} />
                    </Stack>
                </>
            )}
        />
    );
};

export default PatientAssistantPage;
